/**
 * Derive a sample's status from its tests' result codes: the rule the SampleStatusPicker and the
 * SampleStatusSelector both apply when their form collects `TestStatusAggregate`. A sample is never moved to a
 * "higher" status automatically, and a manually-terminal status (voided / preauthorized / failed) is never
 * overwritten.
 */

/**
 * @param {string|Array<string>|undefined} pCodes - a list of codes, or a comma-separated string of them
 * @return {Array<string>} the trimmed, non-empty codes
 */
function normalizeCodeList(pCodes)
{
	const tmpList = Array.isArray(pCodes) ? pCodes : String(pCodes || '').split(',');
	return tmpList.map((pCode) => String(pCode).trim()).filter((pCode) => pCode.length > 0);
}

/**
 * @param {string|Array<string>|undefined} pUnfinishedTestResults - a field's `PictForm.UnfinishedTestResults`
 * @return {Array<string>} the result codes that count as a test that isn't finished
 */
function normalizeUnfinishedTestResults(pUnfinishedTestResults)
{
	return normalizeCodeList(pUnfinishedTestResults);
}

/**
 * Where a field keeps the status a person last picked: beside its own value, so it saves with the form.
 *
 * @param {string} pValueAddress - the field's marshal-relative value address
 * @return {string} the address of the picked status
 */
function getPickedStatusAddress(pValueAddress)
{
	return `${pValueAddress}__PickedStatus`;
}

/**
 * Whether the field keeps its own status when the sample record loads, rather than taking the sample's. A field opts
 * in with `PictForm.KeepSavedStatuses`: it then keeps a status a person picked, whatever it is, plus any status the
 * list names (e.g. Passed, Failed), which covers forms saved before picks were recorded. Any other value, a default
 * included, still follows the sample.
 *
 * @param {string} pFieldStatus - the field's current status code
 * @param {string|Array<string>|undefined} pKeepSavedStatuses - the field's `PictForm.KeepSavedStatuses`
 * @param {string} [pPickedStatus] - the status a person last picked in this field, if any
 * @return {boolean} true to leave the field alone
 */
function keepsFieldStatusOnSampleLoad(pFieldStatus, pKeepSavedStatuses, pPickedStatus)
{
	if (!pFieldStatus || pKeepSavedStatuses === undefined || pKeepSavedStatuses === null || pKeepSavedStatuses === false)
	{
		return false;
	}
	return (pFieldStatus === pPickedStatus) || normalizeCodeList(pKeepSavedStatuses).includes(pFieldStatus);
}

/**
 * @param {Array<string>} pTestResults - each test's result code, '' for a test with none yet
 * @param {string} pSampleStatus - the field's current status code
 * @param {{ UnfinishedTestResults?: string|Array<string> }} [pOptions] - UnfinishedTestResults: result codes that
 *   count as a test that isn't finished (e.g. `SQTY`, a deviation a person must judge), so they never roll up
 *   to Passed on their own
 * @return {string|null} the new status code, or null to leave the field alone
 */
function computeSampleStatusFromTestResults(pTestResults, pSampleStatus, pOptions)
{
	const tmpUnfinished = normalizeUnfinishedTestResults(pOptions && pOptions.UnfinishedTestResults);
	let tmpSeenPendingTest = false;
	let tmpSeenEmptyTest = false;
	let tmpSeenFinalizedTest = false;
	let tmpSeenFailingTest = false;
	let tmpSeenPassingTest = false;
	for (const tmpTestResult of pTestResults)
	{
		if (!tmpTestResult)
		{
			tmpSeenPendingTest = true;
			tmpSeenEmptyTest = true;
			break;
		}
		// A result that still needs a person: the test ran, so the sample is in testing, and a failure elsewhere still counts.
		if (tmpUnfinished.includes(tmpTestResult))
		{
			tmpSeenPendingTest = true;
			tmpSeenFinalizedTest = true;
			continue;
		}
		if (tmpTestResult.startsWith('FAI'))
		{
			tmpSeenFailingTest = true;
			tmpSeenFinalizedTest = true;
		}
		if (tmpTestResult.startsWith('PAS'))
		{
			tmpSeenPassingTest = true;
			tmpSeenFinalizedTest = true;
		}
	}
	if (tmpSeenFailingTest)
	{
		if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC')
		{
			return 'FAIL';
		}
	}
	else if (tmpSeenPendingTest)
	{
		// Only unfinished results left: Passed was a person's call on them, so it stays.
		if (!tmpSeenEmptyTest && ((pSampleStatus == 'PASS') || (pSampleStatus == 'PASC')))
		{
			return null;
		}
		const tmpNewSampleStatus = tmpSeenFinalizedTest ? 'TEST' : 'PEND';
		if (!pSampleStatus || pSampleStatus != 'VOID' && pSampleStatus != 'PREA' && pSampleStatus != 'FAIL' && pSampleStatus != 'FAIC' && pSampleStatus != tmpNewSampleStatus)
		{
			return tmpNewSampleStatus;
		}
	}
	else if (tmpSeenPassingTest)
	{
		if (!pSampleStatus || pSampleStatus == 'N/A' || pSampleStatus == 'LOGD' || pSampleStatus == 'RECV' || pSampleStatus == 'INFO' || pSampleStatus == 'PEND' || pSampleStatus == 'TEST' || pSampleStatus == 'TSTR')
		{
			return 'PASS';
		}
	}
	return null;
}

module.exports = { computeSampleStatusFromTestResults, normalizeUnfinishedTestResults, keepsFieldStatusOnSampleLoad, getPickedStatusAddress };
